/**
 * Whānau-state classification (ADR-010) — pure logic, SPA reference port.
 *
 * Mirrors the server's src/reporting/whanauState.ts (whanau-factor-api) so the
 * dev/mock aggregates match the live ones. Collapses a whānau's factor states
 * into one dominant state: plurality of risk/change/protective; not-assessed
 * excluded; a tie is 'mixed'; nothing assessed is 'unassessed'.
 */
import type { FactorStateKind } from "../framework/types";
import type {
  WhanauState,
  WhanauStateDistribution,
  WhanauStateTransition,
} from "./reportTypes";

export type FactorTally = {
  risk: number;
  change: number;
  protective: number;
};

/** Display/iteration order for the five whānau-state buckets. */
export const WHANAU_STATE_ORDER: readonly WhanauState[] = [
  "risk",
  "change",
  "protective",
  "mixed",
  "unassessed",
];

export function classifyWhanauState(counts: FactorTally): WhanauState {
  const assessed = counts.risk + counts.change + counts.protective;
  if (assessed === 0) return "unassessed";
  const max = Math.max(counts.risk, counts.change, counts.protective);
  const leaders = (["risk", "change", "protective"] as const).filter(
    (k) => counts[k] === max,
  );
  return leaders.length === 1 ? leaders[0] : "mixed";
}

export function tallyStates(states: Iterable<FactorStateKind>): FactorTally {
  const tally: FactorTally = { risk: 0, change: 0, protective: 0 };
  for (const kind of states) {
    if (kind === "risk" || kind === "change" || kind === "protective") {
      tally[kind] += 1;
    }
  }
  return tally;
}

export function emptyWhanauStateDistribution(): WhanauStateDistribution {
  return {
    risk: 0,
    change: 0,
    protective: 0,
    mixed: 0,
    unassessed: 0,
    total: 0,
  };
}

/** A 5×5 transition record initialised to zero. */
export function emptyWhanauStateTransition(): WhanauStateTransition {
  const make = () =>
    Object.fromEntries(WHANAU_STATE_ORDER.map((s) => [s, 0])) as Record<
      WhanauState,
      number
    >;
  return Object.fromEntries(
    WHANAU_STATE_ORDER.map((s) => [s, make()]),
  ) as WhanauStateTransition;
}
